import { Card, Checkbox, Spin, Typography } from 'antd';
import FormField from 'components/FormField';
import LocationSelector from 'components/LocationSelector';
import { MediaSelector } from 'components/MediaSelector';
import { DESTINATION_MEDIA, DESTINATION_MEDIA_RATIO } from 'constants/media-sizes.constants';
import { PurchaseStockPhotoWarningModal } from 'components/StockPhotoWarning';
import React, { useCallback } from 'react';
import type { DestinationTravelThemeSchema } from 'typings/destinations/DestinationTravelThemeSchema';
import styles from './DestinationEditorBasicInfo.module.css';
import { DestinationBasicInfoCommonFields } from './DestinationBasicInfoCommonFields';
import { DestinationTravelThemesCard } from './DestinationTravelThemesCard';
import { useDestinationBasicInfoForm } from './use-destination-basic-info-form';
import { useDestinationEditor } from './DestinationEditorContext';
import { DestinationEditorTabActions } from './DestinationEditorTabActions';

const { Text } = Typography;

type CountryDestinationBasicInfoProps = Readonly<{
    onClickDiscard: (hasUnsavedChanges: boolean) => void;
}>;

export function CountryDestinationBasicInfo({ onClickDiscard }: CountryDestinationBasicInfoProps) {
    const {
        destination,
        location,
        fieldErrors,
        taglineContent,
        setTaglineContent,
        descriptionContent,
        setDescriptionContent,
        isDirty,
        isSaving,
        isContentCurated,
        isTogglingContentCurated,
        isEditing,
        hasContent,
        imageToPurchase,
        isAddingStockPhotoWarningShown,
        setIsAddingStockPhotoWarningShown,
        handleUpdateCoverImage,
        handleSave,
        handleAddPurchasedPhoto,
        handleUpdateField,
        handleUpdateTaglineOrDescription,
        handleToggleContentCurated,
        handleUpdateEditor,
        handleLocationChange,
    } = useDestinationBasicInfoForm();
    const { updateDestination } = useDestinationEditor();

    const handleTravelThemesUpdate = useCallback(
        (themes: DestinationTravelThemeSchema[]) => {
            updateDestination((prev) => ({
                ...prev,
                travel_themes: themes,
            }));
        },
        [updateDestination]
    );

    const coverPhotoLocation =
        location ??
        (destination.name
            ? {
                  name: destination.name,
                  detail: destination.name,
                  fullDetail: destination.name,
                  countryCode: '',
                  lat: 0,
                  lon: 0,
              }
            : null);

    return (
        <>
            <div className={styles.root}>
                <div className={styles.content}>
                    <Card
                        title={<Text strong>Basic Info</Text>}
                        extra={
                            <Checkbox
                                checked={isContentCurated}
                                onChange={handleToggleContentCurated}
                                disabled={!isEditing || isTogglingContentCurated || !hasContent}>
                                Curated content {isTogglingContentCurated ? <Spin size='small' /> : null}
                            </Checkbox>
                        }>
                        <FormField label='Country' error={fieldErrors.name}>
                            <LocationSelector
                                value={location}
                                placeholder='Search for a country'
                                hasError={!!fieldErrors.name}
                                onChange={handleLocationChange}
                                searchMode='countries'
                            />
                        </FormField>
                        <DestinationBasicInfoCommonFields
                            fieldErrors={fieldErrors}
                            taglineContent={taglineContent}
                            setTaglineContent={setTaglineContent}
                            descriptionContent={descriptionContent}
                            setDescriptionContent={setDescriptionContent}
                            destination={destination}
                            handleUpdateTaglineOrDescription={handleUpdateTaglineOrDescription}
                            handleUpdateField={handleUpdateField}
                            handleUpdateEditor={handleUpdateEditor}
                            showPopularity={false}
                        />
                    </Card>
                </div>
                <Card title='Cover photo' className={styles.coverCard}>
                    <MediaSelector
                        initialMediaSource='UNSPLASH'
                        showCropperOnEdit={false}
                        disallowedSources={['FAVORITES']}
                        location={coverPhotoLocation}
                        includeVideos={false}
                        onSelect={handleUpdateCoverImage}
                        onDelete={() => handleUpdateCoverImage(null)}
                        media={destination.cover_image ?? null}
                        ratio={DESTINATION_MEDIA_RATIO}
                    />
                </Card>
                {isEditing && destination.id ? (
                    <div className={styles.themesCard}>
                        <DestinationTravelThemesCard
                            destinationId={destination.id}
                            travelThemes={destination.travel_themes ?? []}
                            onUpdate={handleTravelThemesUpdate}
                        />
                    </div>
                ) : null}
            </div>
            <DestinationEditorTabActions
                tabKey='basicInfo'
                saveLabel='Save Basic Info'
                isSaving={isSaving}
                isSaveDisabled={!isDirty}
                onCancel={() => onClickDiscard(isDirty)}
                onSave={handleSave}
            />
            {isAddingStockPhotoWarningShown && imageToPurchase ? (
                <PurchaseStockPhotoWarningModal
                    photosToPurchase={[imageToPurchase]}
                    onAdd={handleAddPurchasedPhoto}
                    onCancel={() => setIsAddingStockPhotoWarningShown(false)}
                    sizesToUpload={DESTINATION_MEDIA}
                />
            ) : null}
        </>
    );
}
